import React, { type JSX } from 'react';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { Checkbox, Radio, Stack, Tooltip, Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { GroupPath } from './GroupPath';

export const MultiColumnList = (props: any): JSX.Element => {
    const {
        legendData = [],
        type,
        selectedId,
        changeHandler,
        targetKey = 'channelId',
        maxItemsPerColumn,
        isDisabled,
        isLoading,
    } = props;

    const getChildColor = (data: any, colorCode: string, opacity?: any): string => {
        if (!opacity) return '#0088F2';
        return data?.visible ? `${colorCode}${opacity ?? ''}` : '';
    };

    const getRadioColor = (data: any, opacity?: any): string =>
        selectedId === data?.[targetKey] ? `#0088F2${opacity ? 15 : ''}` : '';

    const getColor = (data: any, opacity?: any): string => {
        const colorCode = data?.color ?? '#0088F2';

        if (data?.__isChild) {
            return getChildColor(data, colorCode, opacity);
        }

        if (type === 'radio') {
            return getRadioColor(data, opacity);
        }

        return data?.visible ? `${colorCode}${opacity ?? ''}` : '';
    };

    const getLegendTitle = (legend: any): JSX.Element => (
        <Tooltip title={legend?.name} placement="top">
            {legend?.name}
        </Tooltip>
    );

    const getLegendSubtitle = (legend: any): JSX.Element[] | undefined =>
        props?.multiDevice
            ? [
                  <GroupPath
                      key={`${legend?.[targetKey]}-${legend?.parentId ?? 'root'}-path`}
                      deviceName={legend?.deviceName}
                      pathName={legend?.groupPath}
                  />,
              ]
            : undefined;

    const getLegendRightComponent = (legend: any, childKey: string | undefined): JSX.Element => (
        <Typography>{!legend?.parentId && childKey ? legend?.[childKey]?.length : ''}</Typography>
    );

    const getLegendSelectionMode = (legend: any): 'child' | 'radio' | 'checkbox' => {
        if (legend?.__isChild) return 'child';
        return type === 'radio' ? 'radio' : 'checkbox';
    };

    const flattenedLegendData = legendData.flatMap((item: any) => {
        const childKey = props?.childKey;
        const children = childKey ? item?.[childKey] : null;
        const isSelected = item?.[targetKey] === selectedId;

        if (type !== 'radio' || !Array.isArray(children) || children.length === 0) return [item];

        const selectedChildren = isSelected
            ? children.map((child: any) => ({
                  ...child,
                  __isChild: true,
                  parentId: item?.[targetKey],
              }))
            : [];

        return [item, ...selectedChildren];
    });

    const getLegendIcon = (legend: any): JSX.Element => {
        const selectionMode = getLegendSelectionMode(legend);

        const checkboxSx = {
            color: 'white',
            '&.Mui-checked': {
                color: legend?.color ?? 'white',
            },
        };

        const iconByMode: Record<'child' | 'radio' | 'checkbox', JSX.Element> = {
            child: (
                <Checkbox
                    checked={Boolean(legend?.visible)}
                    disabled={isDisabled}
                    onChange={(e) => changeHandler(e, legend?.[targetKey], legend?.parentId)}
                    sx={checkboxSx}
                />
            ),
            radio: (
                <Radio
                    checked={legend?.[targetKey] === selectedId}
                    onChange={(e) => changeHandler(e, legend?.[targetKey])}
                    disabled={isDisabled}
                />
            ),
            checkbox: (
                <Checkbox
                    checked={legend?.visible}
                    onChange={(e) => changeHandler(e, legend?.[targetKey])}
                    disabled={isDisabled}
                    sx={checkboxSx}
                />
            ),
        };

        return iconByMode[selectionMode];
    };

    const renderLegendItem = (legend: any, childKey: string | undefined): JSX.Element => (
        <InfoListItem
            key={`${legend?.[targetKey]}-${legend?.parentId ?? 'root'}`}
            title={getLegendTitle(legend)}
            subtitle={getLegendSubtitle(legend)}
            className={isDisabled ? 'disabled' : ''}
            rightComponent={getLegendRightComponent(legend, childKey)}
            backgroundColor={getColor(legend, 30)}
            icon={getLegendIcon(legend)}
            statusColor={getColor(legend)}
            sx={{
                height: legend?.__isChild ? '44px' : '54px',
                pl: legend?.__isChild ? 4 : 2,
                '.BluiInfoListItem-title': {
                    width: 'calc(100% - 22px)',
                },
            }}
        />
    );

    const renderColumns = (items: any[], columnSize: number, itemWidth: string): JSX.Element[] => {
        const numItems = items.length;
        const numColumns = Math.ceil(numItems / columnSize);
        const columns: JSX.Element[] = [];

        for (let i = 0; i < numColumns; i++) {
            const startIndex = i * columnSize;
            const endIndex = Math.min(startIndex + columnSize, numItems);
            const columnItems = items.slice(startIndex, endIndex);
            const childKey = props?.childKey;

            columns.push(
                <ul key={i} style={{ margin: 0, padding: 0, width: itemWidth, flexBasis: itemWidth }}>
                    {columnItems.map((legend: any) => renderLegendItem(legend, childKey))}
                </ul>
            );
        }

        return columns;
    };
    const columnSize =
        typeof maxItemsPerColumn === 'number' && maxItemsPerColumn > 0 ? maxItemsPerColumn : flattenedLegendData.length;

    const columnCount = Math.max(1, Math.ceil(flattenedLegendData.length / columnSize));
    const ulWidth = `${100 / columnCount}%`;
    const columns = renderColumns(flattenedLegendData, columnSize, ulWidth);

    return isLoading ? (
        <Stack sx={{ margin: '5px' }} spacing={1} width="100%">
            {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} height={30} />
            ))}
        </Stack>
    ) : (
        <div className="d-flex">{columns}</div>
    );
};
